/**
 * WellSky Integration Engine API (uses global axios client + auth token).
 */
import api from "./client";

const base = "/integrations/wellsky";

export const triggerWellskySync = (agencyId) =>
  api.post(`/integrations/wellsky/sync-now/${agencyId}`);

export const getClientCarePlan = (wellSkyId, agencyId) =>
  api.get(`/integrations/wellsky/company/client-data/careplan/${wellSkyId}`, {
    params: { agencyId },
  });

export const getClientCondition = (wellSkyId, agencyId) =>
  api.get(`/integrations/wellsky/company/client-data/condition/${wellSkyId}`, {
    params: { agencyId },
  });

export const getClientMedications = (wellSkyId, agencyId, options = {}) => {
  const params = { agencyId };
  if (options.refresh) params.refresh = "true";
  return api.get(`/integrations/wellsky/company/client-data/medication/${wellSkyId}`, {
    params,
  });
};

export const getClientContacts = (wellSkyId, agencyId) =>
  api.get(`/integrations/wellsky/company/client-data/relatedperson/${wellSkyId}`, {
    params: { agencyId },
  });

export const getWellSkyClientData = (resourceType, wellSkyId, options = {}) => {
  const params = {};
  if (options.refresh) params.refresh = "true";
  return api.get(
    `/integrations/wellsky/company/client-data/${resourceType}/${wellSkyId}`,
    { params },
  );
};

const withActiveLocationParams = (params = {}) => {
  const raw = localStorage.getItem("activeLocation");
  let activeLocationId = "";
  try {
    const parsed = raw ? JSON.parse(raw) : null;
    activeLocationId = String(parsed?.id || parsed?._id || "").trim();
  } catch {
    activeLocationId = "";
  }
  return activeLocationId
    ? { ...params, locationId: activeLocationId }
    : { ...params };
};

export const integrationApi = {
  list: () => api.get(`${base}/list`).then((r) => r.data),
  connect: (body) => api.post(`${base}/connect`, body).then((r) => r.data),
  test: (body) => api.post(`${base}/test`, body).then((r) => r.data),
  status: (agencyId) => api.get(`${base}/status/${agencyId}`).then((r) => r.data),
  logs: (agencyId, params) =>
    api.get(`${base}/logs/${agencyId}`, { params }).then((r) => r.data),
  syncNow: (agencyId) =>
    api.post(`${base}/sync-now/${agencyId}`).then((r) => r.data),
  pause: (agencyId) => api.patch(`${base}/pause/${agencyId}`).then((r) => r.data),
  resume: (agencyId) => api.patch(`${base}/resume/${agencyId}`).then((r) => r.data),
  toggleEvv: (agencyId, evvWriteEnabled) =>
    api
      .patch(`${base}/toggle-evv/${agencyId}`, { evvWriteEnabled })
      .then((r) => r.data),
  setDefaultFolder: (agencyId, defaultFolderId) =>
    api
      .patch(`${base}/default-folder/${agencyId}`, { defaultFolderId })
      .then((r) => r.data),
  evvEvents: (agencyId, params) =>
    api.get(`${base}/evv-events/${agencyId}`, { params }).then((r) => r.data),
  clockEvents: (agencyId, params) =>
    api.get(`${base}/evv-events/${agencyId}`, { params }).then((r) => r.data),
  retryEvent: (eventId) =>
    api.post(`${base}/evv-events/${eventId}/retry`).then((r) => r.data),
  health: (agencyId) =>
    api.get(`${base}/health/${agencyId}`).then((r) => r.data),
  pushStatus: (jobId) =>
    api.get(`${base}/push-status/${jobId}`).then((r) => r.data),
  chart: (agencyId) =>
    api.get(`${base}/metrics/chart/${agencyId}`).then((r) => r.data),
  agencies: () =>
    api.get("/admin/agencies").then((r) => r.data?.agencies || r.data || []),
  companyStatus: () =>
    api
      .get(`${base}/company/status`, { params: withActiveLocationParams() })
      .then((r) => r.data),
  companySyncNow: () =>
    api
      .post(`${base}/company/sync-now`, null, {
        params: withActiveLocationParams(),
      })
      .then((r) => r.data),
  companySyncStatus: (jobId) =>
    api
      .get(`${base}/company/sync-status/${jobId}`, {
        params: withActiveLocationParams(),
      })
      .then((r) => r.data),
  companyRecords: (params) =>
    api
      .get(`${base}/company/records`, {
        params: withActiveLocationParams(params),
      })
      .then((r) => r.data),
  records: (agencyId, params) =>
    api.get(`${base}/records/${agencyId}`, { params }).then((r) => r.data),
};
